//askSmart-ui/src/pages/SMRTDocs.tsx
import React, { useEffect, useRef, useState } from "react";
import AdminSMRTDocsView from "./AdminSMRTDocsView";
import SMRTDocsSigningView from "../components/SMRTDocsSigningView";
import SMRTDocsFileHistory from "../components/SMRTDocsFileHistory";
import { SMRTDocsPlacedField } from "../components/SMRTDocsFieldPlacementModal";
import { buildSignedPdf, sha256OfString } from "../utils/smrtdocsPdf";
import {
  openSmrtDocsFile,
  smrtDocsCertificateUrl,
} from "../utils/smrtdocsFile";
import { useAuth } from "../hooks/useAuth";
import { getNotifications, addNotification, getMergedSteps } from "../components/SMRTDocsTrafficModal";
import { resolveServiceSteps, resolveServiceStepsAt } from "./smrtdocsServices";
import CollaboratorsPanel from "../components/CollaboratorsPanel";
import InviteCollaboratorModal from "../components/InviteCollaboratorModal";
import "./ManageProfiles.css";
import "./SMRTDocs.css";

// A service shared with the logged-in user, surfaced as its own tab.
type SharedTab = {
  key: string; // stable tab id
  ownerUserId: string;
  serviceIndex: number;
  accessLevel: "view" | "full";
};

// Normalises a shared UserCollaborator row's per-service grants, migrating any
// legacy {serviceIndices, accessLevel} shape.
function sharedGrants(row: any): { serviceIndex: number; accessLevel: "view" | "full" }[] {
  if (Array.isArray(row?.serviceAccess)) return row.serviceAccess;
  if (Array.isArray(row?.serviceIndices)) {
    const level = row.accessLevel === "full" ? "full" : "view";
    return row.serviceIndices.map((i: number) => ({ serviceIndex: i, accessLevel: level }));
  }
  return [];
}

type UserTrafficItem = {
  id: string;
  docKey: string;
  docLabel: string;
  message: string;
};

function getUserRedItems(userId: string, services: any[], assigned: any, userState: any, overrides: any): UserTrafficItem[] {
  const items: UserTrafficItem[] = [];
  if (!userId || !assigned) return items;

  const serviceIndices: number[] =
    assigned.mode === "single" ? [assigned.value] :
    assigned.mode === "multi" ? assigned.values : [];

  const files = userState?.files || {};
  const issued = userState?.issued || {};

  for (const idx of serviceIndices) {
    const svc = services[idx];
    // Resolved, not raw: a Sub Service's lines live on its Master, so its own
    // data.steps holds only what it has added — nothing at all, usually.
    const serviceSteps = resolveServiceSteps(services, svc);
    if (serviceSteps.length === 0) continue;
    const mergedSteps = getMergedSteps(serviceSteps, userId, userState?.localDocs, idx);
    for (const step of mergedSteps) {
      for (const field of step.fields || []) {
        const override = overrides[`${userId}_${field.key}`] || {};
        if (override.hidden) continue;

        if (field.type === "certificate") {
          if (!files[field.key] || !issued[field.key]) {
            items.push({ id: `${userId}_${field.key}`, docKey: field.key, docLabel: field.label, message: `Please upload ${field.label}` });
          }
        } else if (field.type === "template") {
          if (typeof issued[field.key] === "object" && issued[field.key]?.status === "Client Received") {
            items.push({ id: `${userId}_${field.key}`, docKey: field.key, docLabel: field.label, message: `Please complete signing for ${field.label}` });
          }
        }
      }
    }
  }
  return items;
}

function getUserYellowItems(userId: string, services: any[], assigned: any, userState: any, overrides: any): UserTrafficItem[] {
  const items: UserTrafficItem[] = [];
  if (!userId || !assigned) return items;

  const serviceIndices: number[] =
    assigned.mode === "single" ? [assigned.value] :
    assigned.mode === "multi" ? assigned.values : [];

  const files = userState?.files || {};
  const issued = userState?.issued || {};
  const approved = userState?.approved || {};

  for (const idx of serviceIndices) {
    const svc = services[idx];
    // Resolved, not raw: a Sub Service's lines live on its Master, so its own
    // data.steps holds only what it has added — nothing at all, usually.
    const serviceSteps = resolveServiceSteps(services, svc);
    if (serviceSteps.length === 0) continue;
    const mergedSteps = getMergedSteps(serviceSteps, userId, userState?.localDocs, idx);
    for (const step of mergedSteps) {
      for (const field of step.fields || []) {
        const override = overrides[`${userId}_${field.key}`] || {};
        if (override.hidden) continue;
        const approvalRequired = override.approvalRequired !== undefined ? override.approvalRequired : field.approvalRequired;

        if (field.type === "info") {
          // Info not yet issued
          if (!issued[field.key]) {
            items.push({ id: `${userId}_${field.key}`, docKey: field.key, docLabel: field.label, message: `${field.label} will be provided shortly` });
          }
        } else if (field.type === "certificate") {
          // Certificate submitted, awaiting admin approval
          if (files[field.key] && issued[field.key] && approvalRequired && !approved[field.key]) {
            items.push({ id: `${userId}_${field.key}`, docKey: field.key, docLabel: field.label, message: `${field.label} is under review` });
          }
        } else if (field.type === "template") {
          // Template submitted, awaiting admin approval
          if (typeof issued[field.key] === "object" && issued[field.key]?.status === "Client Submitted" && approvalRequired) {
            items.push({ id: `${userId}_${field.key}`, docKey: field.key, docLabel: field.label, message: `${field.label} is under review` });
          }
          // Template not yet sent (admin hasn't uploaded or issued)
          if (!files[field.key] || !issued[field.key]) {
            items.push({ id: `${userId}_${field.key}_notsent`, docKey: field.key, docLabel: field.label, message: `${field.label} will be provided shortly` });
          }
        }
      }
    }
  }
  return items;
}

function getUserGreenItems(userId: string, services: any[], assigned: any, userState: any, overrides: any): UserTrafficItem[] {
  const items: UserTrafficItem[] = [];
  if (!userId || !assigned) return items;

  const serviceIndices: number[] =
    assigned.mode === "single" ? [assigned.value] :
    assigned.mode === "multi" ? assigned.values : [];

  const files = userState?.files || {};
  const issued = userState?.issued || {};

  for (const idx of serviceIndices) {
    const svc = services[idx];
    // Resolved, not raw: a Sub Service's lines live on its Master, so its own
    // data.steps holds only what it has added — nothing at all, usually.
    const serviceSteps = resolveServiceSteps(services, svc);
    if (serviceSteps.length === 0) continue;
    const mergedSteps = getMergedSteps(serviceSteps, userId, userState?.localDocs, idx);
    for (const step of mergedSteps) {
      for (const field of step.fields || []) {
        const override = overrides[`${userId}_${field.key}`] || {};
        if (override.hidden) continue;

        // Info documents issued by admin, not yet read
        if (field.type === "info" && files[field.key] && issued[field.key]) {
          const id = `user_green_${userId}_${field.key}`;
          items.push({ id, docKey: field.key, docLabel: field.label, message: `${field.label} is ready for your review` });
        }
      }
    }
  }

  // Filter out items already marked as read
  const readSet = new Set(getNotifications().filter((n) => n.read).map((n) => n.id));
  return items.filter((item) => !readSet.has(item.id));
}

// GET a JSON payload, treating an HTTP error status as a failure. The bare
// `fetch(...).then(r => r.json())` pattern this replaces parsed error bodies
// as data (or hung the caller in a permanent "loading" state).
async function fetchJson(url: string): Promise<any> {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`${url} → ${res.status}`);
  return res.json();
}

const LOAD_MAX_ATTEMPTS = 3;
const LOAD_RETRY_BASE_DELAY_MS = 500;

// Mount-time loads fail transiently under a managed / RBI browser right after
// login. These are idempotent GETs, so retry briefly (exponential backoff with
// jitter) before surfacing the failure to the caller.
async function fetchJsonWithRetry(url: string): Promise<any> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= LOAD_MAX_ATTEMPTS; attempt++) {
    try {
      return await fetchJson(url);
    } catch (error) {
      lastError = error;
      if (attempt < LOAD_MAX_ATTEMPTS) {
        const delayMs = Math.round(
          LOAD_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1) * (0.5 + Math.random() * 0.5),
        );
        console.error(
          `SMRT Docs load attempt ${attempt}/${LOAD_MAX_ATTEMPTS} failed, retrying in ${delayMs}ms:`,
          error,
        );
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }
  }
  throw lastError;
}

export default function SMRTDocs() {
  const [services, setServices] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<any>({});
  const { user, org: _org, roles } = useAuth();
  const userId = user?.userID;
  // A pure collaborator (joined via a collaborator invite) only sees the service
  // tabs they can access — no Assigned tab and no Collaborator (sharing) tab.
  const isCollaborator =
    Array.isArray(roles?.orgRoles) && roles.orgRoles.includes("Collaborator");
  const [trafficModal, setTrafficModal] = useState<"red" | "yellow" | "green" | null>(null);
  const [selectedGreenIds, setSelectedGreenIds] = useState<string[]>([]);
  // Active tab: an own-service index (number), "assigned", "collaborator", or a
  // shared-service tab key ("shared:<ownerUserId>:<serviceIndex>"). One at a time.
  const [activeTab, setActiveTab] = useState<number | string | null>(null);
  // UserCollaborator rows where I'm the collaborator (each granted service
  // becomes its own tab labelled "Service / Owner").
  const [sharedRows, setSharedRows] = useState<any[]>([]);
  // Owner display names for shared tabs (userId -> {name,email}).
  const [owners, setOwners] = useState<Record<string, { name: string; email: string }>>({});
  const [inviteOpen, setInviteOpen] = useState(false);
  const [collabRefresh, setCollabRefresh] = useState(0);
  const [refresh, setRefresh] = useState(0);
  // Gates the default-tab effect until services/assignments have loaded, so a
  // pre-load fallback ("assigned") isn't computed and then pinned on first
  // paint instead of the user's first service.
  const [loaded, setLoaded] = useState(false);
  // Mount-time load failed after retries — drives the error card + Retry
  // button in place of a silently blank module. Bumping loadAttempt refires
  // the load effect.
  const [loadError, setLoadError] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [profileComplete, setProfileComplete] = useState<boolean | null>(null);
  const [userState, setUserState] = useState<any>({});
  const [overrides, setOverrides] = useState<any>({});
  // Flat list of documents (across the org) where the current user is in
  // the signer roster. Powers the "Assigned Documents" section. The signer
  // metadata is carried alongside so the row can render an order-enforced
  // Sign button for whichever signer is currently active.
  const [assignedDocs, setAssignedDocs] = useState<
    {
      key: string;
      label: string;
      ownerUserId: string;
      /** The service the document is filed under. Sub Services share their
       *  Master's field keys, so the key alone does not identify a document. */
      serviceId?: string;
      signers: any[];
      currentSignerIndex: number;
      status: string;
      blobName?: string;
      docName?: string;
      placements: SMRTDocsPlacedField[];
      history: any[];
      // Audit entries + approval fields surfaced from the owner's
      // user-state so the Certificate button on this row can build the
      // execution record without a second fetch.
      audit: any[];
      approvedBy?: string;
      approvedAt?: string;
    }[]
  >([]);
  /** True once the signer sweep has finished — before that, empty means
   *  "not looked yet", which is not the same as "nothing assigned". */
  const [assignedDocsLoaded, setAssignedDocsLoaded] = useState(false);

  // Load the views shared WITH me (UserCollaborator rows where I'm the
  // collaborator). Each granted service becomes its own shared tab.
  useEffect(() => {
    if (!userId) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/user-collaborators/shared-with-me`, {
      credentials: "include",
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setSharedRows(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [userId]);

  // Resolve sharing-owner display names for the shared tab labels.
  useEffect(() => {
    if (sharedRows.length === 0) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/approved_users`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => {
        const arr = d.users || d || [];
        const map: Record<string, { name: string; email: string }> = {};
        for (const u of arr) {
          const id = u.user_id || u.id;
          if (id) map[id] = { name: u.name || "", email: u.email || "" };
        }
        setOwners(map);
      })
      .catch(() => {});
  }, [sharedRows]);

  // Flatten shared rows into one tab per (owner, service) grant.
  const sharedTabs: SharedTab[] = sharedRows.flatMap((row) =>
    sharedGrants(row).map((g) => ({
      key: `shared:${row.ownerUserId}:${g.serviceIndex}`,
      ownerUserId: row.ownerUserId,
      serviceIndex: g.serviceIndex,
      accessLevel: g.accessLevel,
    })),
  );

  const tabStorageKey = userId ? `smrtdocs-user-tab-${userId}` : null;

  // Default the active tab to the last tab this user viewed (if still valid),
  // otherwise their first own service; otherwise the first shared service;
  // otherwise Assigned (non-collaborator) or nothing. Gated on `loaded` so the
  // pre-load fallback isn't computed and pinned before assignments arrive.
  useEffect(() => {
    if (!loaded) return;
    const current = userId ? assignments[userId] : null;
    let indices: number[] = [];
    if (current?.mode === "single") indices = [current.value];
    else if (current?.mode === "multi") indices = current.values || [];

    const sharedKeys = sharedRows.flatMap((row) =>
      sharedGrants(row).map((g) => `shared:${row.ownerUserId}:${g.serviceIndex}`),
    );

    setActiveTab((prev) => {
      // Keep a still-valid explicit selection.
      if (!isCollaborator && (prev === "assigned" || prev === "collaborator")) return prev;
      if (typeof prev === "number" && indices.includes(prev)) return prev;
      if (typeof prev === "string" && sharedKeys.includes(prev)) return prev;
      // Restore the last tab this user viewed, if it's still valid.
      const saved = tabStorageKey ? localStorage.getItem(tabStorageKey) : null;
      if (saved) {
        if (!isCollaborator && (saved === "assigned" || saved === "collaborator")) return saved;
        if (saved.startsWith("shared:") && sharedKeys.includes(saved)) return saved;
        const n = Number(saved);
        if (!Number.isNaN(n) && indices.includes(n)) return n;
      }
      // Otherwise default to the first assigned service.
      if (indices.length > 0) return indices[0];
      if (sharedKeys.length > 0) return sharedKeys[0];
      return isCollaborator ? null : "assigned";
    });
  }, [assignments, userId, isCollaborator, sharedRows, loaded, tabStorageKey]);

  // Persist the active tab so it's restored on the next visit.
  useEffect(() => {
    if (activeTab === null || !tabStorageKey) return;
    localStorage.setItem(tabStorageKey, String(activeTab));
  }, [activeTab, tabStorageKey]);

  const ownerLabel = (id: string) => owners[id]?.name || owners[id]?.email || id;
  const serviceName = (idx: number) => services[idx]?.name || `Service ${idx + 1}`;
  const activeShared =
    typeof activeTab === "string" && activeTab.startsWith("shared:")
      ? sharedTabs.find((t) => t.key === activeTab) || null
      : null;

  // History modal target — same component used by AdminSMRTDocsView.
  // Carries the originating assigned-doc reference so the Certificate
  // action inside the history modal's kebab menu can rebuild the
  // execution record.
  const [historyOpen, setHistoryOpen] = useState<{
    label: string;
    items: any[];
    doc?: any;
  } | null>(null);

  // Drives the field-fill signing view (and its built-in confirmation
  // overlay) when the current user clicks Sign on a row in Assigned
  // Documents.
  const [signingFlow, setSigningFlow] = useState<{
    fieldKey: string;
    ownerUserId: string;
    serviceId?: string;
    documentName: string;
    blobName: string;
    placements: SMRTDocsPlacedField[];
    mySignerIndex: number;
    mySignerName: string;
  } | null>(null);

  // Set when a signature submission fails to persist. Without this a rejected
  // save looked identical to a successful one — the modal closed, the row
  // stayed unsigned, and the user had no idea anything had gone wrong.
  const [signError, setSignError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoadError(false);
    Promise.all([
      fetchJsonWithRetry(`${process.env.REACT_APP_BACKEND_URL}/smrtdocs/services`),
      fetchJsonWithRetry(`${process.env.REACT_APP_BACKEND_URL}/smrtdocs/assignments`),
      fetchJsonWithRetry(`${process.env.REACT_APP_BACKEND_URL}/smrtdocs/overrides`),
    ])
      .then(([svcData, asgnData, ovData]) => {
        if (cancelled) return;
        setServices(svcData.services || []);
        setAssignments(asgnData.assignments || {});
        setOverrides(ovData.overrides || {});
        setLoaded(true);
      })
      .catch(err => {
        // Retries exhausted — surface the Retry card rather than leaving the
        // module blank with setLoaded(true) unreached.
        console.error("Failed to load SMRT Docs data:", err);
        if (!cancelled) setLoadError(true);
      });
    return () => { cancelled = true; };
  }, [loadAttempt]);

  const lastUserStateHash = useRef("");
  useEffect(() => {
    if (!userId) return;
    const fetchState = () => {
      // No retry wrapper: this already re-polls every 12s, but an HTTP error
      // must not be parsed as user state.
      fetchJson(`${process.env.REACT_APP_BACKEND_URL}/smrtdocs/user-state/${encodeURIComponent(userId)}`)
        .then(data => {
          const { files, issued, history: hist, reminders, approved, notified, localDocs } = data;
          const hash = JSON.stringify({ files, issued, hist, reminders, approved, notified, localDocs });
          if (hash !== lastUserStateHash.current) {
            setUserState(data);
            lastUserStateHash.current = hash;
          }
        })
        .catch(err => console.error("Failed to load user state:", err));
    };
    fetchState();
    let interval = setInterval(fetchState, 12000);
    const onVisibility = () => {
      clearInterval(interval);
      if (document.visibilityState === "visible") {
        fetchState();
        interval = setInterval(fetchState, 12000);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => { clearInterval(interval); document.removeEventListener("visibilitychange", onVisibility); };
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const assignments = await fetchJsonWithRetry(
          `${process.env.REACT_APP_BACKEND_URL}/user-profile-assignments`,
        );
        const assignment = assignments.find(
          (a: any) => a.userId === userId || a.user_id === userId,
        );
        if (!assignment?.profileTemplateId) {
          setProfileComplete(false);
          return;
        }
        const saved = await fetchJsonWithRetry(
          `${process.env.REACT_APP_BACKEND_URL}/profile-submissions/${userId}/${assignment.profileTemplateId}`,
        );
        const status = saved?.status;
        setProfileComplete(status === "submitted");
      } catch {
        setProfileComplete(false);
      }
    })();
  }, [userId]);

  // Walk every doc-owning user's state and collect items where the current
  // user is listed as a signer. Uses the existing per-user state endpoint
  // so no new backend routes are needed.
  useEffect(() => {
    if (!userId) return;
    const ownerIds = Object.keys(assignments || {});
    if (ownerIds.length === 0) {
      setAssignedDocs([]);
      setAssignedDocsLoaded(true);
      return;
    }
    // Labels per service as well as overall: the same key can carry a
    // different label on a service that overrides its Master's line.
    const labelByKey: Record<string, string> = {};
    const labelByService: Record<string, Record<string, string>> = {};
    for (const svc of services) {
      const own: Record<string, string> = {};
      for (const step of resolveServiceSteps(services, svc)) {
        for (const field of step.fields || []) {
          if (field?.key) {
            labelByKey[field.key] = field.label || field.key;
            own[field.key] = field.label || field.key;
          }
        }
      }
      if (svc?.id) labelByService[svc.id] = own;
    }
    let cancelled = false;
    Promise.all(
      ownerIds.map((ownerId) =>
        // bySlice: each service's documents kept apart. The merged view
        // collapses sibling Sub Services that share a key, which hid
        // documents waiting for this user's signature.
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/smrtdocs/user-state/${encodeURIComponent(ownerId)}?bySlice=1`,
          { credentials: "include" },
        )
          .then((r) => (r.ok ? r.json() : null))
          .then((data) => ({ ownerId, data }))
          .catch(() => ({ ownerId, data: null })),
      ),
    ).then((results) => {
      if (cancelled) return;
      const docs: typeof assignedDocs = [];
      for (const { ownerId, data } of results) {
        if (ownerId === userId || !data) continue;
        // One entry per service. A backend without bySlice answers with the
        // merged maps only; that is read as a single slice of no named service.
        const slices: [string | undefined, any][] =
          data.slices && typeof data.slices === "object"
            ? Object.entries(data.slices)
            : [[undefined, data]];
        for (const [serviceId, slice] of slices) {
          const issued = slice?.issued || {};
          const files = slice?.files || {};
          const ownerPlacements = slice?.placements || {};
          const ownerHistory = slice?.history || {};
          const ownerAudit = slice?.audit || {};
          for (const key of Object.keys(issued)) {
            const it = issued[key];
            if (!it || !Array.isArray(it.signers)) continue;
            if (it.signers.some((s: any) => s && s.userId === userId)) {
              docs.push({
                key,
                label:
                  (serviceId && labelByService[serviceId]?.[key]) ||
                  labelByKey[key] ||
                  it.name ||
                  key,
                ownerUserId: ownerId,
                serviceId,
                signers: it.signers,
                currentSignerIndex:
                  typeof it.currentSignerIndex === "number"
                    ? it.currentSignerIndex
                    : 0,
                status: it.status || "Client Received",
                blobName: files[key]?.blobName,
                docName: files[key]?.name,
                placements: Array.isArray(ownerPlacements[key])
                  ? ownerPlacements[key]
                  : [],
                history: Array.isArray(ownerHistory[key])
                  ? ownerHistory[key]
                  : [],
                audit: Array.isArray(ownerAudit[key])
                  ? ownerAudit[key]
                  : [],
                approvedBy: it.approvedBy || "",
                approvedAt: it.approvedAt || "",
              });
            }
          }
        }
      }
      setAssignedDocs(docs);
      setAssignedDocsLoaded(true);
    });
    return () => {
      cancelled = true;
    };
    // `refresh` is bumped after a signing submit so the row's signedAt
    // state reflects the new value and the Sign button drops away.
  }, [userId, assignments, services, refresh]);

  const assigned = userId ? assignments[userId] : null;

  // An index with no service behind it is a dangling assignment — the service
  // was deleted, or positions shifted under it. Rendered, it became a phantom
  // tab labelled "Service N" from the index alone, holding nothing. Kept while
  // services are still loading, or every tab would blink out on first paint.
  const assignedIndices: number[] = (
    assigned?.mode === "single" ? [assigned.value] :
    assigned?.mode === "multi" ? (assigned.values || []) : []
  ).filter((idx) => services.length === 0 || Boolean(services[idx]));

  const showAssignedTab = assignedDocs.length > 0;

  // "assigned" is the pre-load fallback and can also be what was restored from
  // the last visit. If the tab has since gone — nothing left to sign — move to
  // somewhere that exists rather than leaving a pane with no tab above it.
  // Held until the sweep has run: empty before that only means "not looked".
  useEffect(() => {
    if (activeTab !== "assigned" || showAssignedTab || !assignedDocsLoaded) return;
    if (assignedIndices.length > 0) setActiveTab(assignedIndices[0]);
    else if (sharedTabs.length > 0) setActiveTab(sharedTabs[0].key);
    else setActiveTab("collaborator");
    // Read positionally from arrays rebuilt each render; the lengths are what
    // actually change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    showAssignedTab,
    assignedDocsLoaded,
    assignedIndices.length,
    sharedTabs.length,
  ]);

  const redItems = userId ? getUserRedItems(userId, services, assigned, userState, overrides) : [];
  const yellowItems = userId ? getUserYellowItems(userId, services, assigned, userState, overrides) : [];
  const greenItems = userId ? getUserGreenItems(userId, services, assigned, userState, overrides) : [];

  const TITLES: Record<string, string> = { red: "Action Required", yellow: "Waiting on Admin", green: "Unread" };
  const STATUS_COLORS: Record<string, string> = { red: "#c62828", yellow: "#d4a017", green: "#1faa59" };

  const modalItems = trafficModal === "red" ? redItems : trafficModal === "yellow" ? yellowItems : trafficModal === "green" ? greenItems : [];
  const allGreenIds = greenItems.map((i) => i.id);
  const allSelected = allGreenIds.length > 0 && allGreenIds.every((id) => selectedGreenIds.includes(id));

  const handleMarkAsRead = () => {
    for (const id of selectedGreenIds) {
      addNotification({ id, userId: userId || "", docKey: id, type: "green", message: "Marked as read", timestamp: Date.now(), read: true });
    }
    setSelectedGreenIds([]);
    setRefresh((n) => n + 1);
  };

  if (loadError) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h2>SMRT Docs</h2>
        <p style={{ color: "#9aa0a6", fontSize: "1rem", marginTop: "2rem" }}>
          SMRT Docs couldn't load. Please check your connection and try again.
        </p>
        <button
          type="button"
          onClick={() => setLoadAttempt((n) => n + 1)}
          style={{
            marginTop: "1rem",
            padding: "8px 24px",
            borderRadius: 6,
            border: "1px solid #2a2a2a",
            background: "#007bff",
            color: "#fff",
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (profileComplete === null) return null;

  if (!profileComplete) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h2>SMRT Docs</h2>
        <p style={{ color: "#9aa0a6", fontSize: "1rem", marginTop: "2rem" }}>
          Please complete your profile before accessing SMRT Docs.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: "2rem" }}>
      <h2 style={{ margin: 0 }}>SMRT Docs</h2>

      {/* Traffic light summary */}
      <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
        <div
          title="Action Required"
          style={{ display: "flex", alignItems: "center", gap: 6, background: "#1c1c1c", border: "1px solid #2a2a2a", borderRadius: 6, padding: "4px 12px", cursor: "pointer" }}
          onClick={() => { setTrafficModal("red"); setSelectedGreenIds([]); }}
        >
          <span style={{ fontSize: "1rem" }}>🔴</span>
          <span style={{ color: "#fff", fontSize: "0.85rem", fontWeight: 600 }}>{redItems.length}</span>
        </div>
        <div
          title="Waiting on Admin"
          style={{ display: "flex", alignItems: "center", gap: 6, background: "#1c1c1c", border: "1px solid #2a2a2a", borderRadius: 6, padding: "4px 12px", cursor: "pointer" }}
          onClick={() => { setTrafficModal("yellow"); setSelectedGreenIds([]); }}
        >
          <span style={{ fontSize: "1rem" }}>🟡</span>
          <span style={{ color: "#fff", fontSize: "0.85rem", fontWeight: 600 }}>{yellowItems.length}</span>
        </div>
        <div
          title="Unread Items"
          style={{ display: "flex", alignItems: "center", gap: 6, background: "#1c1c1c", border: "1px solid #2a2a2a", borderRadius: 6, padding: "4px 12px", cursor: "pointer" }}
          onClick={() => { setTrafficModal("green"); setSelectedGreenIds([]); }}
        >
          <span style={{ fontSize: "1rem" }}>🟢</span>
          <span style={{ color: "#fff", fontSize: "0.85rem", fontWeight: 600 }}>{greenItems.length}</span>
        </div>
      </div>

      {/* Tabs: one per assigned service, then Assigned + Collaborator */}
      <div className="template-tabs" style={{ marginTop: "16px" }}>
        {assignedIndices.map((idx) => (
          <div
            key={idx}
            className={`template-tab ${activeTab === idx ? "active" : ""}`}
            onClick={() => setActiveTab(idx)}
          >
            <span>{services[idx]?.name || `Service ${idx + 1}`}</span>
          </div>
        ))}
        {sharedTabs.map((t) => (
          <div
            key={t.key}
            className={`template-tab ${activeTab === t.key ? "active" : ""}`}
            onClick={() => setActiveTab(t.key)}
            title={`${serviceName(t.serviceIndex)} — shared by ${ownerLabel(t.ownerUserId)}`}
          >
            <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.15 }}>
              <span>{serviceName(t.serviceIndex)}</span>
              <span style={{ fontSize: "0.7rem", opacity: 0.65 }}>
                {ownerLabel(t.ownerUserId)}
              </span>
            </span>
          </div>
        ))}
        {!isCollaborator && (
          <>
            {/* Assigned is a home for documents this user has to sign as a
                second or third signer, outside their own services. With none
                of those, there is nothing for the tab to hold — so it isn't
                shown at all rather than offering an empty room. */}
            {showAssignedTab && (
              <div
                className={`template-tab ${activeTab === "assigned" ? "active" : ""}`}
                onClick={() => setActiveTab("assigned")}
              >
                <span>Assigned</span>
              </div>
            )}
            <div
              className={`template-tab ${activeTab === "collaborator" ? "active" : ""}`}
              onClick={() => setActiveTab("collaborator")}
            >
              <span>Collaborator</span>
            </div>
            {activeTab === "collaborator" && (
              <div
                className="template-tab template-tab-plus"
                title="Invite Collaborator"
                onClick={() => setInviteOpen(true)}
              >
                +
              </div>
            )}
          </>
        )}
      </div>

      {typeof activeTab === "number" && (
        !services[activeTab] ? (
          <div className="smrtdocs-content" style={{ color: "#999", marginTop: "10px" }}>No documents</div>
        ) : (
          <div className="smrtdocs-content" style={{ marginTop: "10px" }}>
            <div className="smrtdocs-user-view">
              <AdminSMRTDocsView
                template={{ steps: resolveServiceStepsAt(services, activeTab) }}
                services={services}
                assigned={assigned}
                viewServiceIndex={activeTab}
                setViewServiceIndex={(i: number) => setActiveTab(i)}
                isUserView={true}
                userId={userId}
                userName={user?.name}
                userEmail={user?.email}
              />
            </div>
          </div>
        )
      )}

      {activeShared && (
        <div className="smrtdocs-content" style={{ marginTop: "10px" }}>
          <div
            className={`smrtdocs-user-view${
              activeShared.accessLevel === "view" ? " smrtdocs-readonly" : ""
            }`}
          >
            <AdminSMRTDocsView
              template={{ steps: resolveServiceStepsAt(services, activeShared.serviceIndex) }}
              services={services}
              assigned={{ mode: "single", value: activeShared.serviceIndex }}
              viewServiceIndex={activeShared.serviceIndex}
              setViewServiceIndex={() => {}}
              isUserView={true}
              userId={activeShared.ownerUserId}
              userName={ownerLabel(activeShared.ownerUserId)}
              userEmail={owners[activeShared.ownerUserId]?.email}
            />
          </div>
        </div>
      )}

      {activeTab === "collaborator" && (
        <div className="smrtdocs-content">
          <CollaboratorsPanel ownerUserId={userId || ""} refreshKey={collabRefresh} />
        </div>
      )}

      {/* Collaborator with nothing shared yet: they've been linked to an owner's
          view but no service has been granted to them, so there are no tabs to
          show. Render an explanation instead of a blank page. */}
      {isCollaborator && sharedTabs.length === 0 && (
        <div className="smrtdocs-content" style={{ marginTop: "10px" }}>
          <div style={{ color: "#999", padding: "24px 12px", textAlign: "center", lineHeight: 1.5 }}>
            No documents have been shared with you yet.
            <br />
            The person who invited you needs to grant you access to one or more of
            their services.
          </div>
        </div>
      )}

      {activeTab === "assigned" && (
        <div className="smrtdocs-content">
        {signError && (
          <div
            role="alert"
            style={{
              background: "#fdecea",
              border: "1px solid #c62828",
              color: "#c62828",
              borderRadius: 4,
              padding: "10px 12px",
              marginBottom: 12,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <span style={{ flex: 1 }}>{signError}</span>
            <button
              className="btn-toggle"
              onClick={() => setSignError("")}
              style={{ flexShrink: 0 }}
            >
              Dismiss
            </button>
          </div>
        )}
        {assignedDocs.length === 0 ? (
        <div style={{ color: "#999" }}>No documents assigned for signing</div>
      ) : (
        <div className="smrtdocs-user-view" style={{ marginTop: "10px" }}>
          <div className="template-list smrtdocs-editor smrtdocs-scope">
            <div className="template-card" style={{ marginBottom: 0 }}>
              {/* 5-child layout matches the My Documents pane (the
                  CSS hides the 2nd child via
                  .smrtdocs-user-view .template-grid-header > div:nth-child(2)
                  and uses a 4-column grid: Document / Status / File / Actions). */}
              <div className="template-grid-header">
                <div>Document</div>
                <div>Status Requirements</div>
                <div>Status</div>
                <div>File</div>
                <div>Actions</div>
              </div>
              {assignedDocs.map((d) => {
                const current = d.signers[d.currentSignerIndex];
                const isCurrent =
                  current && current.userId === userId;
                const alreadySigned = d.signers.some(
                  (s: any) =>
                    s && s.userId === userId && s.signedAt,
                );
                // Surface the same status text the template user sees on
                // their My Documents pane.
                const statusLabel = d.status === "Active"
                  ? "Active"
                  : d.status === "Client Submitted"
                    ? "Client Submitted"
                    : "Client Received";
                return (
                  <div
                    key={`${d.ownerUserId}_${d.serviceId || ""}_${d.key}`}
                    className="template-field-row"
                  >
                    <div>{d.label}</div>
                    <div></div>
                    <div>{statusLabel}</div>
                    <div>
                      {d.blobName ? (
                        <span
                          className="smrtdocs-file-link"
                          onClick={() => {
                            // Open the assigned file straight from the backend
                            // against the document owner's userId, so the signer
                            // sees the file they were assigned to.
                            const { opened, url } = openSmrtDocsFile(
                              d.ownerUserId,
                              d.blobName || "",
                            );
                            // Popup blocked — navigate this tab instead rather
                            // than letting the click look dead.
                            if (!opened) window.location.href = url;
                          }}
                        >
                          {d.docName || d.label}
                        </span>
                      ) : (
                        <span style={{ color: "#9aa0a6" }}>—</span>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      {isCurrent && !alreadySigned && (
                        <button
                          className="btn-toggle smrtdocs-btn-issue"
                          title="Sign"
                          onClick={() =>
                            setSigningFlow({
                              fieldKey: d.key,
                              ownerUserId: d.ownerUserId,
                              serviceId: d.serviceId,
                              documentName: d.docName || d.label,
                              blobName: d.blobName || "",
                              placements: d.placements,
                              mySignerIndex: d.currentSignerIndex,
                              mySignerName:
                                (d.signers[d.currentSignerIndex]
                                  ?.name as string) || "",
                            })
                          }
                        >
                          <span
                            style={{
                              fontSize: "14px",
                              lineHeight: 1,
                              padding: "0 6px",
                            }}
                          >
                            Sign
                          </span>
                        </button>
                      )}
                      {alreadySigned &&
                        (d.status === "Active" ||
                          d.status === "Completed") && (
                          /* Real anchor: the certificate is generated
                             server-side from stored state and served as
                             an attachment — the download pattern that
                             survives an RBI / managed browser. */
                          <a
                            className="btn-toggle"
                            title="Certificate of Completion"
                            style={{
                              background: "#f3f4f6",
                              borderColor: "#d1d5db",
                              color: "#1f2937",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              textDecoration: "none",
                            }}
                            href={smrtDocsCertificateUrl(
                              d.ownerUserId,
                              d.key,
                              d.serviceId,
                            )}
                          >
                            <span
                              style={{
                                fontSize: "16px",
                                lineHeight: 1,
                              }}
                            >
                              📜
                            </span>
                          </a>
                        )}
                      <button
                        className="btn-toggle smrtdocs-btn-history"
                        title="View History"
                        onClick={() =>
                          setHistoryOpen({
                            label: d.label,
                            items: d.history,
                            doc: d,
                          })
                        }
                      >
                        ↻
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
        </div>
      )}

      {historyOpen && (
        <SMRTDocsFileHistory
          isOpen={true}
          onClose={() => setHistoryOpen(null)}
          history={historyOpen.items}
          title={historyOpen.label}
          isUserView={true}
          documentType="template"
          onView={async (fileObj: any) => {
            // Open the file (signed or original) in a new tab via the
            // backend proxy against the owning admin's blob — the same
            // helper the file link on the assigned-doc row uses.
            const ownerId = historyOpen?.doc?.ownerUserId;
            if (!fileObj?.blobName || !ownerId) return;
            const { opened, url } = openSmrtDocsFile(
              ownerId,
              fileObj.blobName,
            );
            if (!opened) window.location.href = url;
          }}
          certificateUrl={
            historyOpen.doc &&
            (historyOpen.doc.status === "Active" ||
              historyOpen.doc.status === "Completed")
              ? smrtDocsCertificateUrl(
                  historyOpen.doc.ownerUserId,
                  historyOpen.doc.key,
                  historyOpen.doc.serviceId,
                )
              : undefined
          }
        />
      )}

      {signingFlow && (
        <SMRTDocsSigningView
          isOpen={true}
          documentBlobName={signingFlow.blobName}
          documentName={signingFlow.documentName}
          ownerUserId={signingFlow.ownerUserId}
          placements={signingFlow.placements}
          mySignerIndex={signingFlow.mySignerIndex}
          mySignerName={signingFlow.mySignerName}
          onClose={() => setSigningFlow(null)}
          onSubmit={async (result) => {
            // Round-trip the owner's user-state: fetch, mark the current
            // signer complete, advance the index, then PUT it back. Mirrors
            // the in-place mutation the admin view performs, just routed
            // at the owner's userId.
            try {
              const ownerId = signingFlow.ownerUserId;
              // Read and write THIS document's service only. Unscoped, the
              // read returned every service merged — a sibling tenancy's
              // record on the same key could stand in for this one — and the
              // write left the server to guess the service; an admin signer's
              // save would then put the merged maps into whichever it chose.
              const sliceQuery = signingFlow.serviceId
                ? `?serviceId=${encodeURIComponent(signingFlow.serviceId)}`
                : "";
              const stateRes = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/smrtdocs/user-state/${encodeURIComponent(ownerId)}${sliceQuery}`,
                { credentials: "include" },
              );
              if (!stateRes.ok) throw new Error("Failed to load owner state");
              const ownerState = await stateRes.json();
              const issued = { ...(ownerState.issued || {}) };
              const audit: Record<string, Record<string, unknown>[]> = {
                ...(ownerState.audit || {}),
              };
              const it = issued[signingFlow.fieldKey];
              const ownerFiles: Record<string, any> = {
                ...(ownerState.files || {}),
              };
              if (it && Array.isArray(it.signers)) {
                const idx =
                  typeof it.currentSignerIndex === "number"
                    ? it.currentSignerIndex
                    : 0;
                const signedAtIso = new Date().toISOString();
                // Per-signer verification code — deterministic SHA256
                // over (documentHash + userId + signedAt). Stored full
                // on the signer + audit; PDF embed uses first 16 chars.
                const signerForCode = it.signers[idx] || {};
                const signerUserId =
                  (signerForCode.userId as string) ||
                  (userId as string) ||
                  "";
                const verificationCode = await sha256OfString(
                  ((it.documentHash as string) || "") +
                    signerUserId +
                    signedAtIso,
                );
                // Per-placement values this signer just stamped.
                // Signature images live on `signatureImages`; initial /
                // date / title / text values live on `fieldValues` so
                // the final PDF embeds every captured field.
                const placements = signingFlow.placements || [];
                const mySigImages: Record<string, string> = {};
                const myFieldValues: Record<string, string> = {};
                for (const p of placements) {
                  if (p.signerIndex !== idx) continue;
                  const v = result.fieldValues?.[p.id];
                  if (typeof v !== "string" || !v) continue;
                  if (p.type === "signature") {
                    if (v.startsWith("data:image")) mySigImages[p.id] = v;
                  } else {
                    myFieldValues[p.id] = v;
                  }
                }
                const updatedSigners = it.signers.map((s: any, i: number) =>
                  i === idx
                    ? {
                        ...s,
                        signedAt: signedAtIso,
                        signatureImages: {
                          ...(s.signatureImages || {}),
                          ...mySigImages,
                        },
                        fieldValues: {
                          ...(s.fieldValues || {}),
                          ...myFieldValues,
                        },
                        verificationCode,
                      }
                    : s,
                );
                const allDone = idx + 1 >= updatedSigners.length;
                let issuedForKey: Record<string, unknown> = {
                  ...it,
                  signers: updatedSigners,
                  currentSignerIndex: allDone ? idx : idx + 1,
                  status: allDone ? "Client Submitted" : "Client Received",
                };
                // On final signer, build the signed PDF, upload it as
                // the new File reference. Original blob is preserved
                // (different blobName) and stashed on issued.originalFile.
                // Track the signed-file ref (and whether the upload
                // actually succeeded) so the new history Signed entry
                // below can reference the same blob that ends up on
                // the owner's `files` record.
                let signedFileRef: any = null;
                if (allDone) {
                  try {
                    const origFile = ownerFiles[signingFlow.fieldKey];
                    if (origFile?.blobName) {
                      const baseRes = await fetch(
                        `${process.env.REACT_APP_BACKEND_URL}/smrtdocs/files/${encodeURIComponent(signingFlow.ownerUserId)}/_?blobName=${encodeURIComponent(origFile.blobName)}&format=base64`,
                        { credentials: "include" },
                      );
                      if (baseRes.ok) {
                        const { base64 } = await baseRes.json();
                        const bin = atob(base64);
                        const origBytes = new Uint8Array(bin.length);
                        for (let i = 0; i < bin.length; i++)
                          origBytes[i] = bin.charCodeAt(i);
                        const signedBytes = await buildSignedPdf({
                          originalBytes: origBytes,
                          placements,
                          signers: updatedSigners,
                        });
                        const blob = new Blob([signedBytes], {
                          type: "application/pdf",
                        });
                        const form = new FormData();
                        form.append(
                          "file",
                          blob,
                          (origFile.name || "document.pdf").replace(
                            /\.pdf$/i,
                            "_signed.pdf",
                          ),
                        );
                        const upRes = await fetch(
                          `${process.env.REACT_APP_BACKEND_URL}/smrtdocs/files/${encodeURIComponent(signingFlow.ownerUserId)}/${encodeURIComponent(signingFlow.fieldKey)}`,
                          {
                            method: "POST",
                            credentials: "include",
                            body: form,
                          },
                        );
                        if (upRes.ok) {
                          const ref = await upRes.json();
                          signedFileRef = {
                            name: ref.name,
                            type: ref.type,
                            blobName: ref.blobName,
                            uploadedAt: ref.uploadedAt,
                          };
                          ownerFiles[signingFlow.fieldKey] = signedFileRef;
                          issuedForKey = {
                            ...issuedForKey,
                            originalFile: origFile,
                          };
                        }
                      }
                    }
                  } catch (pdfErr) {
                    console.error(
                      "Failed to build/upload signed PDF:",
                      pdfErr,
                    );
                  }
                }
                issued[signingFlow.fieldKey] = issuedForKey;
                // Append-only audit entry for this signer. Backend
                // stamps ipAddress from the request and dedupes by
                // (userId, signingOrderPosition).
                const signerEntry = it.signers[idx] || {};
                const ownerFile =
                  (ownerState.files || {})[signingFlow.fieldKey] || {};
                const auditEntry = {
                  userId:
                    (signerEntry.userId as string) ||
                    (userId as string) ||
                    "",
                  orgId: (ownerState.orgID as string) || "",
                  documentId: signingFlow.fieldKey,
                  documentVersion:
                    (ownerFile.uploadedAt as string) ||
                    (ownerFile.blobName as string) ||
                    "",
                  documentHash: (it.documentHash as string) || "",
                  signingOrderPosition:
                    typeof signerEntry.signingOrderPosition === "number"
                      ? signerEntry.signingOrderPosition
                      : idx,
                  signedAt: signedAtIso,
                  ipAddress: "",
                  userAgent:
                    typeof navigator !== "undefined"
                      ? navigator.userAgent
                      : "",
                  consentText:
                    "By selecting Adopt and Sign, I agree that the signature and initials will be the electronic representation of my signature and initials for all purposes when I (or my agent) use them on documents, including legally binding contracts.",
                  signatureType: result.signatureType,
                  verificationCode,
                };
                audit[signingFlow.fieldKey] = [
                  ...(Array.isArray(audit[signingFlow.fieldKey])
                    ? audit[signingFlow.fieldKey]
                    : []),
                  auditEntry,
                ];
                // Append the signed snapshot to the owner's history so
                // the History tab shows the freshly-completed cycle
                // alongside the Unsigned snapshot saved at Send time.
                // Only runs when the signed PDF upload above succeeded
                // (signedFileRef is non-null), avoiding orphan entries
                // pointing at the unsigned blob.
                const updatedHistory: Record<string, any[]> = {
                  ...(ownerState.history || {}),
                };
                if (allDone && signedFileRef) {
                  // Snapshot each signer's name + signedAt onto the
                  // entry so the History expand renders a per-signer
                  // line without depending on the live issued record
                  // (which is cleared on R).
                  const signersSnapshot = updatedSigners.map(
                    (s: any) => ({
                      name: s?.name || s?.email || "",
                      signedAt: s?.signedAt || "",
                    }),
                  );
                  updatedHistory[signingFlow.fieldKey] = [
                    ...(Array.isArray(
                      updatedHistory[signingFlow.fieldKey],
                    )
                      ? updatedHistory[signingFlow.fieldKey]
                      : []),
                    {
                      file: signedFileRef,
                      kind: "signed",
                      status: "Client Submitted",
                      submittedBy:
                        (user as any)?.name ||
                        (user as any)?.email ||
                        "",
                      submittedAt: signedAtIso,
                      date: signedAtIso,
                      signers: signersSnapshot,
                    },
                  ];
                }
                const saveRes = await fetch(
                  `${process.env.REACT_APP_BACKEND_URL}/smrtdocs/user-state/${encodeURIComponent(ownerId)}`,
                  {
                    method: "PUT",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      ...(signingFlow.serviceId
                        ? { serviceId: signingFlow.serviceId }
                        : {}),
                      files: ownerFiles,
                      issued,
                      approved: ownerState.approved || {},
                      notified: ownerState.notified || {},
                      history: updatedHistory,
                      reminders: ownerState.reminders || {},
                      localDocs:
                        ownerState.localDocs || {
                          attachedFields: {},
                          steps: [],
                        },
                      placements: ownerState.placements || {},
                      audit,
                    }),
                  },
                );
                // A rejected save must not read as a completed signature.
                if (!saveRes.ok) {
                  throw new Error(`Save rejected with ${saveRes.status}`);
                }
              } else {
                // The document went away between opening the modal and
                // submitting (withdrawn, or reissued). Say so rather than
                // closing as though it had been signed.
                throw new Error("Document is no longer available for signing");
              }
              setSignError("");
            } catch (err) {
              console.error("Failed to submit signature:", err);
              setSignError(
                "Your signature could not be saved, so this document is still unsigned. Please try again — if it keeps failing, contact your administrator.",
              );
            }
            setSigningFlow(null);
            setRefresh((n) => n + 1);
          }}
        />
      )}

      {/* Traffic light modal */}
      {trafficModal && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: "550px", maxHeight: "80vh", display: "flex", flexDirection: "column" }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>{TITLES[trafficModal]}</h2>
              <button className="btn-cancel" onClick={() => setTrafficModal(null)}>Close</button>
            </div>

            {/* Green toolbar: Select All + Mark as Read */}
            {trafficModal === "green" && modalItems.length > 0 && (
              <div style={{ padding: "8px 20px", borderBottom: "1px solid #555", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, color: "#9aa0a6", fontSize: "0.8rem", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={() => setSelectedGreenIds(allSelected ? [] : [...allGreenIds])}
                    style={{ accentColor: "#1faa59" }}
                  />
                  Select All
                </label>
                {selectedGreenIds.length > 0 && (
                  <button
                    className="btn-icon"
                    style={{ background: "#1faa59", border: "none", borderRadius: 6, color: "#fff", padding: "6px 20px", fontSize: "0.82rem", cursor: "pointer", whiteSpace: "nowrap", width: "auto", minWidth: 130 }}
                    onClick={handleMarkAsRead}
                  >
                    Mark As Read
                  </button>
                )}
              </div>
            )}

            <div className="smrtdocs-modal-body" style={{ overflowY: "auto", flex: 1 }}>
              {modalItems.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {modalItems.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "8px 12px",
                        background: "#1c1c1c",
                        border: "1px solid #2a2a2a",
                        borderRadius: 6,
                      }}
                    >
                      {trafficModal === "green" && (
                        <input
                          type="checkbox"
                          checked={selectedGreenIds.includes(item.id)}
                          onChange={() => setSelectedGreenIds((prev) => prev.includes(item.id) ? prev.filter((x) => x !== item.id) : [...prev, item.id])}
                          style={{ accentColor: "#1faa59", cursor: "pointer" }}
                        />
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ color: "#fff", fontSize: "0.9rem" }}>{item.docLabel}</div>
                        <div style={{ fontSize: "0.8rem", color: STATUS_COLORS[trafficModal] }}>{item.message}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: "#9aa0a6", textAlign: "center", padding: "20px 0" }}>No items</div>
              )}
            </div>
          </div>
        </div>
      )}

      <InviteCollaboratorModal
        isOpen={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onDone={() => setCollabRefresh((n) => n + 1)}
      />
    </div>
  );
}
